import { Navigate, Route, Routes } from "react-router-dom";
import { useEffect, useState } from "react";
import { auth } from "../firebase-config";
import { LoadingPage } from "./Loading/LoadingPage";
import { InitialUsernameSelector } from "components/Username/InitialUsernameSelector";
import { resetUser } from "app/slices/UserSlice";
import { fetchPickems } from "db/info/pickemInfo";
import { userSubscription, setUserInfoFromDb } from "db/info/userInfo";
import {
  PAGE_CONFIGS,
  PageConfig,
  PageRestriction,
  homePath,
  loginPath,
} from "./PageConfigs";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { Page } from "./Page";

const UNKNOWN_ROUTE_REDIRECT = (
  <Route key="*" path="*" element={<Navigate replace to={homePath} />} />
);

interface Props {
  initialDataLoaded: boolean;
}

export const Main = ({ initialDataLoaded }: Props) => {
  const dispatch = useAppDispatch();
  const [authLoaded, setAuthLoaded] = useState(false);
  const [loggedIn, setLoggedIn] = useState(false);
  const [hasUsername, setHasUsername] = useState(false);
  const username = useAppSelector((state) => state.user.username);

  // Code to update 'Main' user state when authState changes
  useEffect(() => {
    auth.onAuthStateChanged(() => {
      setAuthLoaded(false);
      const uid = auth.currentUser?.uid;
      // If !uid, no current signed in user
      if (!uid) {
        setLoggedIn(false);
        setAuthLoaded(true);
        setHasUsername(false);
        if (!!userSubscription) {
          userSubscription();
        }

        dispatch(resetUser());
        return;
      }

      // Else user signed in, figure if username setup and set state accordingly
      setUserInfoFromDb(uid)
        .then((result) => {
          fetchPickems(uid, "Tmwt2023S1Tmcl");
          fetchPickems(uid, "Tmwt2023S1TmGl");
          fetchPickems(uid, "Tmwt2023S2Tmgl");
          fetchPickems(uid, "Tmwt2023S2Tmcl");
          fetchPickems(uid, "Tmwt2023WcWorlds");
          fetchPickems(uid, "Ccb2023");
          fetchPickems(uid, "BwlS1");
          fetchPickems(uid, "BwlS2");
          fetchPickems(uid, "Aopl2024");
          fetchPickems(uid, "Ccb2024");
          fetchPickems(uid, "CwpanCymru2024");
          fetchPickems(uid, "Bymcx1");
          fetchPickems(uid, "Bymcx2");
          fetchPickems(uid, "Adlx2");
          fetchPickems(uid, "Meatcx1");
          fetchPickems(uid, "Pzchsx1");
          fetchPickems(uid, "Bipcx3");
          fetchPickems(uid, "Encorex1");
          fetchPickems(uid, "2024TmwtWcPlayoffs");
          fetchPickems(uid, "Tmwt2024WcWorlds");
          fetchPickems(uid, "Bymcx3");
          fetchPickems(uid, "Bymcx4");
          fetchPickems(uid, "Bymcx5");
          fetchPickems(uid, "Bymcx6");
          fetchPickems(uid, "MajiMondayx4");
          fetchPickems(uid, "Bdlx1");
          fetchPickems(uid, "Bymcx7");
          fetchPickems(uid, "Totx1");
          fetchPickems(uid, "Bymcx8");
          fetchPickems(uid, "Ccb2025");
          fetchPickems(uid, "Bymcx9");
          fetchPickems(uid, "CwpanCymrux2");
          fetchPickems(uid, "Itcx1");
          fetchPickems(uid, "Bymcx10");
          fetchPickems(uid, "Tmwt2025S1Playoffs");
          setLoggedIn(true);
          setAuthLoaded(true);
          setHasUsername(result);
        })
        .catch((error: Error) => {
          auth.signOut().then(() => {
            alert(
              "Unable to login, this may be due to a Google Api issue.\nPlease try again later"
            );
          });
        });
    });
  }, []);

  useEffect(() => {
    const newHasUsername = username !== "" && !!username;
    if (hasUsername !== newHasUsername) {
      setHasUsername(newHasUsername);
    }
  }, [username]);

  const allPaths = PAGE_CONFIGS.map((p) => p.path);

  if (!authLoaded || !initialDataLoaded) {
    return (
      <Routes>
        {UNKNOWN_ROUTE_REDIRECT}
        {allPaths.map((p) => (
          <Route key={p} path={p} element={<LoadingPage />} />
        ))}
      </Routes>
    );
  }

  if (loggedIn && !hasUsername) {
    return (
      <Routes>
        {UNKNOWN_ROUTE_REDIRECT}
        {allPaths.map((p) => (
          <Route
            path={p}
            element={
              !!auth.currentUser?.uid ? (
                <InitialUsernameSelector uid={auth.currentUser?.uid} />
              ) : (
                <span>
                  State suggests user is logged in, but auth has no currentUser
                  stored. Note for User - refreshing the page may fix this.
                </span>
              )
            }
          />
        ))}
      </Routes>
    );
  }

  return (
    <Routes>
      {UNKNOWN_ROUTE_REDIRECT}
      {PAGE_CONFIGS.map((config) => getPageRoute(config, loggedIn))}
    </Routes>
  );
};

const getPageElement = (config: PageConfig, loggedIn: boolean) => {
  if (config.restriction === PageRestriction.LOGGED_IN) {
    if (!loggedIn) {
      return <Navigate replace to={loginPath} state={{ prev: config.path }} />;
    }
  } else if (config.restriction === PageRestriction.NOT_LOGGED_IN) {
    if (loggedIn) {
      return <Navigate replace to={homePath} />;
    }
  }

  return <Page config={config} />;
};

const getPageRoute = (config: PageConfig, loggedIn: boolean) => {
  return (
    <Route
      key={config.path}
      path={config.path}
      element={getPageElement(config, loggedIn)}
    />
  );
};
